import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/LanguageContext";

// Product Transfer → "↩ Send back to Production" (packing station / BO / SA).
// Optional reason; the factory sees the order in Production again and must
// sign off + transfer it once more.
export function TransferSendBackButton({ order, size = "sm", className = "" }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const m = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/transfer-send-back`, { reason: reason.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries();
      setOpen(false);
      setReason("");
      toast.success(`↩ ${order.po_number} ${t("sent back to Production")}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send back"),
  });
  return (
    <>
      <Button
        size={size}
        variant="outline"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`transfer-send-back-${order.id}`}
        title={t("Return this order to the factory (Production)")}
        className={`border-rose-300 text-rose-700 hover:bg-rose-50 ${className}`}
      >
        ↩ {t("Send back")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()} data-testid={`transfer-send-back-dialog-${order.id}`}>
          <DialogHeader>
            <DialogTitle>↩ {t("Send back to Production")} · {order.po_number}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {t("The order leaves Product Transfer and returns to the factory's Production list. The factory sign-off is cleared so they must sign off and transfer it again.")}
          </p>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={300}
            rows={3}
            placeholder={t("Reason (optional) — e.g. item never arrived at packing, wrong cover in the bag…")}
            data-testid={`transfer-send-back-reason-${order.id}`}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`transfer-send-back-cancel-${order.id}`}>{t("Cancel")}</Button>
            <Button onClick={() => m.mutate()} disabled={m.isPending} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid={`transfer-send-back-confirm-${order.id}`}>
              {m.isPending ? t("Sending…") : `↩ ${t("Send back to Production")}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
